import { useMemo } from 'react';
import { ROUTES } from '@/app/config/constants';
import { useAppSelector } from '@/app/store/hooks';
import { selectIsSupplierRole, useAuth, useFeatureAccess } from '@/features/authentication';
import { APP_MENU_ITEMS, MEGA_ADMIN_MENU_ITEMS } from '../constants';

/** The sidebar's BIDs entry. */
const BIDS_MENU_ID = 'rfqs';
/** The sidebar's Dashboard entry. */
const DASHBOARD_MENU_ID = 'dashboard';
import { withReadableFeatures } from '../utils/featureMenuAccess';
/*
  `filterByPermissions` and `serverPermissions` were imported here too. Both belonged to the arrangement
  this hook replaced -- a menu filtered by flat permission strings fetched from the server. The filtering
  is now the RBAC grants', through `withReadableFeatures` and `withReadableMasters` below, and
  `filterByPermissions` no longer exists to import.
*/
import { withReadableMasters } from '../utils/masterMenuAccess';
import { filterByRoles } from '../utils/menuSanitize';
import type { SidebarMenuItem } from '../types';

interface UseMenuResult {
  items: SidebarMenuItem[];
  loading: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * The signed-in user's sidebar, built from their role's RBAC grants.
 *
 * One catalogue for every role, filtered by what that role was granted. It used to pick a different
 * hardcoded array per role SLUG and fall through to `GET /sidebar/menu` for anything else — and since
 * the slug vocabulary holds three values while the platform has ten roles, `Approver L1` and every role
 * an administrator creates fell through to an endpoint the platform does not serve, and signed in to an
 * empty sidebar. Nothing fetches a menu now.
 *
 * Mega Admin keeps its own fixed list and skips the grant filter, exactly as before.
 *
 * `loading` and `error` stay in the shape callers expect, both settled: there is no request left to be
 * in flight or to fail, and the sidebar renders on the first paint rather than flashing a spinner.
 * `retry` is likewise a no-op, kept so the error UI it belongs to does not have to be rewritten.
 */
export function useMenu(): UseMenuResult {
  const { isMegaAdmin, user } = useAuth();
  const { canReach } = useFeatureAccess();
  const isSupplierRole = useAppSelector(selectIsSupplierRole);
  const roles = user?.roles;

  /*
    Two passes, both asking the same grants the routes and the screens ask.

    First the features a menu entry names — the FUNCTION main menus, and the TEMPLATE children — kept
    only where the role holds VIEW. Ticking VIEW on Purchase Requests for a role in the RBAC screen is what puts
    it in that role's sidebar; unticking it is what takes it away. Then the masters, whose children
    resolve through their own registry.

    Copied out of the constant so a consumer cannot mutate the shared catalogue.

    When the grants are unknown neither pass drops anything — see `useFeatureAccess` for why that
    direction, and note the routes take it too, so a menu and the page it leads to cannot disagree.
  */
  const items = useMemo<SidebarMenuItem[]>(() => {
    /*
      BIDs for the Supplier role (2026-10-27) are the Supplier Bid Quotation: the same entry, leading to the supplier's
      BIDs. It keeps the entry's own feature -- BIDs in RBAC → Functions -- so unticking BIDs for the Supplier role
      hides it, as for every other role. Every other role keeps the buyer's BIDs exactly as listed. Likewise its Dashboard
      is the Supplier Dashboard (2026-10-29, NB-637).
    */
    const supplierPaths: Readonly<Record<string, string>> = {
      [BIDS_MENU_ID]: ROUTES.SUPPLIER_BIDS,
      [DASHBOARD_MENU_ID]: ROUTES.SUPPLIER_DASHBOARD,
    };
    const served = (isMegaAdmin ? MEGA_ADMIN_MENU_ITEMS : APP_MENU_ITEMS).map((item) => {
      const supplierPath = isSupplierRole ? supplierPaths[item.id] : undefined;
      return supplierPath ? { ...item, path: supplierPath } : { ...item };
    });
    if (isMegaAdmin) return served;

    /*
      The role pass first, and it touches only an entry that declares `requiredRoles` -- today none does
      (Default Template Forms, the last, was removed on 2026-10-16). Every entry without one passes through
      untouched, so the grants decide the menu.
    */
    const byRole = filterByRoles(served, roles ?? []);
    return withReadableMasters(withReadableFeatures(byRole, canReach), canReach);
  }, [canReach, isMegaAdmin, isSupplierRole, roles]);

  return { items, loading: false, error: null, retry: () => {} };
}
